import React, { useRef, useCallback } from "react";
import { IconButton } from "@mui/material";
import { Send, AttachFile } from "@mui/icons-material";
import VoiceChat from "core/components/global/input/voice/voiceChat";
import { FilePreviewCard } from "core/components/global/input/inputBars/FilePreviewCard";
import { ChatInputProps } from "./types";

// Extracted file upload button to prevent unnecessary re-renders
const FileUploadButton = React.memo(({ 
  onFileSelected 
}: { 
  onFileSelected: (file: File) => void 
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = event.target.files?.[0];
  //   if (file) {
  //     onFileSelected(file);
  //   }
  // }, [onFileSelected]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Allowed file types
    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert(`Invalid file type. Allowed: PNG, JPEG, PDF`);
      event.target.value = ""; // reset input
      return;
    }

    // Max file size (5 MB)
    const maxSizeMB = 5;
    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`File size exceeds ${maxSizeMB} MB`);
      event.target.value = "";
      return;
    }

    onFileSelected(file);
  }, [onFileSelected]);


  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div>
      <AttachFile className="cursor-pointer" onClick={triggerFileInput} />
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </div>
  );
});

const ChatInput: React.FC<ChatInputProps> = ({
  userPrompt,
  setUserPrompt,
  postData,
  handleDataFromChild,
  previewFile,
  setPreviewFile,
  theme
}) => {
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      postData();
    }
  }, [postData]);
  
  const handleFileSelected = useCallback((file: File) => {
    setPreviewFile(file);
  }, [setPreviewFile]);

  return (
    <div
      className={`${
        theme.name === "dark-theme"
          ? "bg-[linear-gradient(to_bottom,transparent_0%,#1d1d1d_20%)]"
          : ""
      } p-4`}
    >
      {previewFile && (
        <FilePreviewCard
          file={previewFile}
          onRemove={() => setPreviewFile(null)}
        />
      )}
      <div
        className={`flex items-center gap-2 ${
          theme.name === "dark-theme"
            ? "bg-[linear-gradient(to_bottom,transparent_0%,#1d1d1d_20%)]"
            : "bg-[#faf5ff]"
        } px-1 py-1 rounded-lg border mb-3 ml-5`}
      >
        <input
          placeholder="Type your message..."
          className={`flex-1 outline-none border-none bg-transparent p-2 ${
            theme.name === "dark-theme"
              ? "text-white !important"
              : "text-black"
          }`}
          onChange={(e) => setUserPrompt(e.target.value)}
          value={userPrompt}
          onKeyPress={handleKeyPress}
        />
        <IconButton
          className={`${
            theme.name === "dark-theme"
              ? "text-white !important"
              : "text-black"
          }`}
        >
          <FileUploadButton onFileSelected={handleFileSelected} />
        </IconButton>
        <IconButton
          className={`${
            theme.name === "dark-theme"
              ? "text-white !important"
              : "text-black"
          }`}
        >
          <VoiceChat
            sendDataToParent={handleDataFromChild}
            sendMessageByVoice={postData}
          />
        </IconButton>
        <IconButton
          onClick={postData}
          className={`${
            theme.name === "dark-theme"
              ? "text-white !important"
              : "text-black"
          }`}
        >
          <Send />
        </IconButton>
      </div>
    </div>
  );
};

export default React.memo(ChatInput);